import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'
import { AlreadyExistsError } from 'core/infra/errors/AlreadyExistsError'
import { useAbortController } from 'core/ui/hooks/useAbortController'

import { type PreferredLane } from '../../domain/PreferredLane'
import { addPreferredLane } from '../../use-cases/addPreferredLane'
import { checkPreferredLaneExistence } from '../../use-cases/checkPreferredLaneExistence'
import { deletePreferredLane } from '../../use-cases/deletePreferredLane'

export function usePreferredLane() {
  const abortController = useAbortController()

  const [preferredLaneId, setPreferredLaneId] = useState<string | undefined>(undefined)

  const [saveStatus, setSaveStatus] = useState(RequestStatus.Initial)
  const [checkStatus, setCheckStatus] = useState(RequestStatus.Initial)
  const [removeStatus, setRemoveStatus] = useState(RequestStatus.Initial)

  const isSaving = saveStatus === RequestStatus.Pending
  const isChecking = checkStatus === RequestStatus.Pending
  const isRemoving = removeStatus === RequestStatus.Pending
  const isLoading = isSaving || isChecking || isRemoving

  const savePreferredLane = useCallback(
    async (preferredLane: PreferredLane) => {
      if (isSaving || isChecking) return

      setSaveStatus(RequestStatus.Pending)
      try {
        const newPreferredLane = await addPreferredLane(preferredLane, abortController)
        setSaveStatus(RequestStatus.Done)
        setPreferredLaneId(newPreferredLane?.uuid)
        return newPreferredLane
      } catch (error) {
        if (!abortController.signal.aborted) {
          if (error instanceof AlreadyExistsError) {
            setSaveStatus(RequestStatus.Done)
            throw new Error('This preferred lane already exists', { cause: error })
          }

          setSaveStatus(RequestStatus.Error)
          throw new Error('Could not save this preferred lane', { cause: error })
        }
      }
    },
    [isChecking, isSaving, abortController]
  )

  const checkIfPreferredLaneExists = useCallback(
    async (preferredLane: PreferredLane) => {
      setCheckStatus(RequestStatus.Pending)
      try {
        const uuid = await checkPreferredLaneExistence(preferredLane, abortController)
        setCheckStatus(RequestStatus.Done)
        setPreferredLaneId(uuid)
      } catch (error) {
        if (!abortController.signal.aborted) {
          setCheckStatus(RequestStatus.Error)
          setPreferredLaneId(undefined)
        }
      }
    },
    [abortController]
  )

  const removePreferredLane = useCallback(async () => {
    if (isSaving || isChecking || !preferredLaneId) return

    setRemoveStatus(RequestStatus.Pending)
    try {
      await deletePreferredLane(preferredLaneId, abortController)
      setPreferredLaneId(undefined)
      setRemoveStatus(RequestStatus.Done)
    } catch (error) {
      if (!abortController.signal.aborted) {
        setRemoveStatus(RequestStatus.Error)
        throw new Error('Error while deleting preferred lane', { cause: error })
      }
    }
  }, [isSaving, isChecking, preferredLaneId, abortController])

  return {
    preferredLaneId,
    isChecking,
    isSaving,
    isRemoving,
    isLoading,
    savePreferredLane,
    checkIfPreferredLaneExists,
    removePreferredLane,
  }
}
